// Purchases: customers/{cid}/purchases, one document per thing somebody acquired.
//
// The collection is shared and discriminated by a `collection` field - 'memberships' and
// 'benefitcards' both live here, read through the same _getPurchases in firebase.js. So
// anything true of a purchase in general belongs in this module, and the two listings that
// render them (MembershipListContainer, BenefitCardListsContainer) both import from it
// rather than growing a second copy of the same logic. Card- and membership-specific
// reading stays in benefitcards.js and memberships.js.
//
// Pure by design: no Firebase, no React, no date library.

const num = (value) => {
  const parsed = typeof value === 'number' ? value : parseFloat(String(value === null || value === undefined ? '' : value).replace(',', '.'))
  return isFinite(parsed) ? parsed : null
}

/* The payment */

// A purchase made through the shop carries its payment INLINE as a single `payment` map:
//
//   { amount: 12500, typelabel: 'Online (Paytrail/osuuspankki)',
//     timestamp: '2026.09.04 12:39', type, provider, pp, orderid, id, uid }
//
// Amounts are CENTS. A `payments[]` array does not occur on purchases - only reservations
// and activities carry one - but it is tolerated here so a stray array cannot make the
// column silently read as unpaid.
//
// A purchase with NO payment was added by staff or imported, which for memberships is the
// common case rather than the edge. It gets a sentence of its own instead of a dash.
export const NO_PAYMENT_TEXT = 'Admin/kassa lisännyt'

// Deliberately does NOT consider `row.price`. Price is what the thing LISTS for, not
// evidence that anyone paid it, and reporting it as a payment would show an unpaid
// staff-added purchase as paid. The absence of a payment is the signal.
export const paymentOf = (row) => {
  if (!row) return null
  const payments = Array.isArray(row.payments) ? row.payments.filter(Boolean) : []
  if (payments.length > 0) {
    return {
      cents: payments.reduce((sum, p) => sum + (num(p.amount) || 0), 0),
      how: payments[0].typelabel || payments[0].type || '',
      when: payments[0].timestamp || '',
    }
  }
  const payment = row.payment
  if (!payment || num(payment.amount) === null) return null
  return {
    cents: num(payment.amount),
    how: payment.typelabel || payment.type || '',
    when: payment.timestamp || '',
  }
}

const formatEuros = (cents) => (Math.round(cents) / 100).toFixed(2).replace('.', ',') + ' €'

// payment.timestamp arrives preformatted as 'YYYY.MM.DD HH:mm'. Reordered by hand rather
// than through moment so this module keeps its no-dependency character; anything that does
// not match is passed through untouched, the same guard PersonDetails2.paymentsText uses.
const displayTimestamp = (value, withTime) => {
  const text = String(value || '').trim()
  const match = text.match(/^(\d{4})\.(\d{2})\.(\d{2})([ T](\d{2}:\d{2}))?/)
  if (!match) return text
  const date = match[3] + '.' + match[2] + '.' + match[1]
  return withTime && match[5] ? date + ' ' + match[5] : date
}

// One cell for a purchase listing: what was paid, how, and when. Belongs in a column's
// valueGetter rather than a renderCell, because MUI does not export rendered cells.
//
// `withTime: false` keeps the date but drops the clock time. The card listing needs it: the
// full string does not fit a column sharing its width with eight others, and a truncated
// cell hides the payment METHOD - the part that cannot be recovered from another column -
// while the minute a card was bought is the least useful part of the three.
export const paymentText = (row, { withTime = true } = {}) => {
  const payment = paymentOf(row)
  if (!payment) return NO_PAYMENT_TEXT
  return [formatEuros(payment.cents), payment.how, displayTimestamp(payment.when, withTime)]
    .filter(Boolean)
    .join(' · ')
}

/* Date ranges */

// Purchases are inconsistent about separators: memberships write start/end as 'YYYY-MM-DD'
// while benefit cards write them as 'YYYY.MM.DD', and both write `validuntil` dashed. '.'
// sorts after '-', so comparing the raw strings reports every dotted-start row as broken.
// Stripping the separators leaves a plain YYYYMMDD that orders correctly either way.
export const dateKey = (value) => {
  const digits = String(value || '').replace(/\D/g, '')
  return digits.length >= 8 ? digits.slice(0, 8) : ''
}

// Bad data that exists in production, e.g. start 2024-06-01 with end 2024-05-31. Takes the
// two dates rather than a row, so each listing passes the end IT displays - what counts as
// the end differs between memberships and cards, and that knowledge belongs next to the
// column doing the rendering, not in here.
export const endsBefore = (start, end) => {
  const startKey = dateKey(start)
  const endKey = dateKey(end)
  if (!startKey || !endKey) return false
  return endKey < startKey
}
